import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { IMAGE_SCOPES, listContentImages, uploadContentImage } from "../../../../services/contentServices";
import { contentEditorCardSx, contentCardContentSx, contentSectionTitleSx } from "../contentEditorStyles";
import {
  createImageBlock,
  createImageBlockFromUrl,
  createParagraphBlock,
  moveBlock,
  removeBlock,
  updateBlock,
} from "./articleContentUtils";
import ArticleImageCropDialog from "./ArticleImageCropDialog";
import { ARTICLE_BODY_IMAGE_ASPECT } from "./articleImageLayoutConstants";
import RichParagraphEditor from "./RichParagraphEditor";
import { normalizeParagraphBlock } from "../../../../utils/articleParagraphUtils";

const ARTICLE_IMAGE_ACCEPT = ".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp";

const isValidImageUrl = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) {
    return false;
  }
  try {
    const parsed = new URL(raw);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const ArticleContentEditor = ({ content, onChange, onError, onSuccess }) => {
  const [images, setImages] = useState([]);
  const [imageMenuAnchor, setImageMenuAnchor] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [cropFile, setCropFile] = useState(null);
  const fileInputRef = useRef(null);
  const blocks = content.blocks || [];

  const setBlocks = (nextBlocks) => onChange({ ...content, version: 1, blocks: nextBlocks });

  const loadImages = useCallback(async () => {
    try {
      setImages(await listContentImages(IMAGE_SCOPES.ARTICLES));
    } catch (error) {
      onError(error.message);
    }
  }, [onError]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  const handleUploadImage = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setCropFile(file);
  };

  const handleCropCancel = () => {
    setCropFile(null);
  };

  const handleCropConfirm = async (croppedFile) => {
    try {
      const result = await uploadContentImage(croppedFile, IMAGE_SCOPES.ARTICLES);
      setImages((prev) => [result, ...prev]);
      setBlocks([...blocks, createImageBlock(result)]);
      onSuccess("Immagine aggiunta all'articolo.");
      setCropFile(null);
    } catch (error) {
      onError(error.message);
    }
  };

  const handleSelectLibraryImage = (image) => {
    setBlocks([...blocks, createImageBlock(image)]);
    setLibraryOpen(false);
    onSuccess("Immagine aggiunta all'articolo.");
  };

  const openLibrary = async () => {
    setImageMenuAnchor(null);
    await loadImages();
    setLibraryOpen(true);
  };

  const openLinkDialog = () => {
    setImageMenuAnchor(null);
    setLinkUrl("");
    setLinkDialogOpen(true);
  };

  const handleAddImageFromLink = () => {
    if (!isValidImageUrl(linkUrl)) {
      onError("Inserisci un URL immagine valido (http o https).");
      return;
    }
    setBlocks([...blocks, createImageBlockFromUrl(linkUrl)]);
    setLinkDialogOpen(false);
    setLinkUrl("");
    onSuccess("Immagine aggiunta dall'URL.");
  };

  const renderImage = (block, index) => (
    <Stack spacing={1}>
      <Box
        component="img"
        src={block.publicUrl}
        alt={block.alt || "Immagine articolo"}
        sx={{ width: "100%", maxHeight: 180, objectFit: "cover", borderRadius: 1.5, border: "1px solid #e8edf3" }}
      />
      <TextField
        size="small"
        label="Testo alternativo"
        value={block.alt || ""}
        onChange={(event) => setBlocks(updateBlock(blocks, index, { alt: event.target.value }))}
      />
    </Stack>
  );

  return (
    <Card sx={contentEditorCardSx}>
      <CardContent sx={contentCardContentSx}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography sx={contentSectionTitleSx}>Contenuto articolo</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setBlocks([...blocks, createParagraphBlock()])}
              sx={{ fontWeight: 700 }}
            >
              + PARAGRAFO
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={(event) => setImageMenuAnchor(event.currentTarget)}
              sx={{ fontWeight: 700 }}
            >
              + IMMAGINE
            </Button>
          </Stack>
        </Stack>

        <Menu
          anchorEl={imageMenuAnchor}
          open={Boolean(imageMenuAnchor)}
          onClose={() => setImageMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setImageMenuAnchor(null);
              fileInputRef.current?.click();
            }}
          >
            Carica
          </MenuItem>
          <MenuItem onClick={openLibrary}>
            Libreria
          </MenuItem>
          <MenuItem onClick={openLinkDialog}>
            Link
          </MenuItem>
        </Menu>
        <input
          ref={fileInputRef}
          hidden
          type="file"
          accept={ARTICLE_IMAGE_ACCEPT}
          onChange={handleUploadImage}
        />

        <Stack spacing={1.25}>
          {blocks.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Aggiungi almeno un paragrafo o un&apos;immagine.</Typography>
          ) : (
            blocks.map((block, index) => (
              <Card key={`block-${index}`} variant="outlined" sx={{ ...contentEditorCardSx, borderStyle: "dashed" }}>
                <CardContent sx={{ ...contentCardContentSx, py: 1.25 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Elemento {index + 1} - {block.type === "image" ? "Immagine" : "Paragrafo"}
                    </Typography>
                    <Stack direction="row" spacing={0.25}>
                      <IconButton size="small" disabled={index === 0} onClick={() => setBlocks(moveBlock(blocks, index, "up"))}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" disabled={index === blocks.length - 1} onClick={() => setBlocks(moveBlock(blocks, index, "down"))}>
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setBlocks(removeBlock(blocks, index))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                  {block.type === "image" ? (
                    renderImage(block, index)
                  ) : (
                    <RichParagraphEditor
                      block={normalizeParagraphBlock(block)}
                      onChange={(nextBlock) => setBlocks(updateBlock(blocks, index, nextBlock))}
                    />
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </Stack>

        <Dialog open={libraryOpen} onClose={() => setLibraryOpen(false)} fullWidth maxWidth="md">
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
            Libreria immagini articolo
            <IconButton size="small" onClick={() => setLibraryOpen(false)} aria-label="Chiudi libreria immagini">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {images.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nessuna immagine disponibile. Usa «Carica» per aggiungerne una.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                  gap: 1.5,
                }}
              >
                {images.map((image) => (
                  <Box
                    key={image.objectKey}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSelectLibraryImage(image)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleSelectLibraryImage(image);
                      }
                    }}
                    sx={{
                      borderRadius: 1.5,
                      border: "1px solid #e8edf3",
                      overflow: "hidden",
                      cursor: "pointer",
                      backgroundColor: "#fff",
                      "&:hover": { borderColor: "#0b3d87" },
                    }}
                  >
                    <Box
                      component="img"
                      src={image.publicUrl}
                      alt={image.fileName}
                      sx={{ width: "100%", height: 96, objectFit: "cover", display: "block" }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        px: 0.75,
                        py: 0.75,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {image.fileName}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Aggiungi immagine da URL</DialogTitle>
          <DialogContent dividers>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="URL immagine"
              placeholder="https://..."
              value={linkUrl}
              onChange={(event) => setLinkUrl(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleAddImageFromLink();
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLinkDialogOpen(false)}>Annulla</Button>
            <Button variant="contained" onClick={handleAddImageFromLink} sx={{ bgcolor: "#0b3d87" }}>
              Aggiungi
            </Button>
          </DialogActions>
        </Dialog>
        <ArticleImageCropDialog
          open={Boolean(cropFile)}
          imageFile={cropFile}
          aspectRatio={ARTICLE_BODY_IMAGE_ASPECT}
          title="Ritaglia immagine contenuto"
          onCancel={handleCropCancel}
          onConfirm={handleCropConfirm}
        />
      </CardContent>
    </Card>
  );
};

ArticleContentEditor.propTypes = {
  content: PropTypes.shape({
    version: PropTypes.number,
    blocks: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default ArticleContentEditor;
